import { useState } from 'react';
import { Container, Title, Table, Button, Group, Modal, Stack, Switch, NumberInput, Text, Badge, Textarea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useSubstitutionPreferences, useSetSubstitutionPref, useRemoveSubstitutionPref, useSubstitutionHistory } from '@/hooks/usePortalApi';

export default function SubstitutionPrefs() {
  const { data: prefData } = useSubstitutionPreferences();
  const { data: histData } = useSubstitutionHistory();
  const setPref = useSetSubstitutionPref();
  const removePref = useRemoveSubstitutionPref();
  const [opened, { open, close }] = useDisclosure(false);
  const [productId, setProductId] = useState<number | ''>('');
  const [allowed, setAllowed] = useState(true);
  const [organicOnly, setOrganicOnly] = useState(false);
  const [maxPricePct, setMaxPricePct] = useState<number | ''>('');
  const [notes, setNotes] = useState('');

  const prefs = prefData?.preferences ?? [];
  const history = histData?.history ?? [];

  const handleSave = () => {
    if (!productId) return;
    setPref.mutate({
      product_id: Number(productId), allowed, organic_only: organicOnly,
      max_price_pct: maxPricePct ? Number(maxPricePct) : undefined, notes: notes || undefined,
    }, {
      onSuccess: () => { close(); notifications.show({ title: 'Saved', message: 'Substitution preference saved', color: 'green' }); },
    });
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>Substitution Preferences</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={open}>Add Rule</Button>
      </Group>

      <Table striped highlightOnHover mb="xl">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Product</Table.Th>
            <Table.Th>Allowed</Table.Th>
            <Table.Th>Organic Only</Table.Th>
            <Table.Th>Max Price +%</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {prefs.map((p) => (
            <Table.Tr key={p.id}>
              <Table.Td>{p.product_name}</Table.Td>
              <Table.Td><Badge color={p.substitution_allowed ? 'green' : 'red'}>{p.substitution_allowed ? 'Yes' : 'No'}</Badge></Table.Td>
              <Table.Td>{p.organic_only ? 'Yes' : 'No'}</Table.Td>
              <Table.Td>{p.max_price_increase_pct != null ? `${p.max_price_increase_pct}%` : '-'}</Table.Td>
              <Table.Td>
                <Button size="xs" variant="subtle" color="red" leftSection={<IconTrash size={14} />}
                  onClick={() => removePref.mutate(p.product_id)}>Remove</Button>
              </Table.Td>
            </Table.Tr>
          ))}
          {prefs.length === 0 && (
            <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center">No substitution rules configured</Text></Table.Td></Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      {history.length > 0 && (
        <>
          <Title order={3} mb="sm">Recent Substitution History</Title>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Original</Table.Th>
                <Table.Th>Substitute</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Date</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {history.map((h) => (
                <Table.Tr key={h.id}>
                  <Table.Td>{h.original_product}</Table.Td>
                  <Table.Td>{h.substitute_product}</Table.Td>
                  <Table.Td><Badge>{h.status}</Badge></Table.Td>
                  <Table.Td>{h.created_at ? new Date(h.created_at).toLocaleDateString() : '-'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}

      <Modal opened={opened} onClose={close} title="Add Substitution Rule">
        <Stack>
          <NumberInput label="Product ID" value={productId} onChange={(v) => setProductId(typeof v === 'number' ? v : '')} />
          <Switch label="Allow substitutions" checked={allowed} onChange={(e) => setAllowed(e.currentTarget.checked)} />
          <Switch label="Organic only" checked={organicOnly} onChange={(e) => setOrganicOnly(e.currentTarget.checked)} />
          <NumberInput label="Max price increase (%)" value={maxPricePct} onChange={(v) => setMaxPricePct(typeof v === 'number' ? v : '')} decimalScale={2} />
          <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
          <Button onClick={handleSave} loading={setPref.isPending}>Save</Button>
        </Stack>
      </Modal>
    </Container>
  );
}
